import { z } from "zod";
import { TFile, TObjectBase, TObjectBaseTDate } from "../base";
import {
  TReferenceToStringConverter,
  TRelativeToStringConverter,
} from "../converters";
import {
  TApiTaskMetadataObject,
  TApiTaskDetailMetadataObject,
  TApiContractorGroupMetadata,
  TApiUserMetadata,
  TApiPropertyMetadata,
  TApiCompanyMetadata,
  TApiUnitMetadata,
  TApiRoutineMetadata,
  TApiTaskDetailRelativeMetadata,
  TApiCompanyTagMetadata,
  TApiCompanyTaskTypeMetadata,
  TApiExternalLinkMetadata,
  TApiInvoiceReceiverMetadata,
} from "../common/metadata";
import { TTaskSettings } from "./taskSettings";
import { TVisibility } from "../common/visibility";
import {
  TApiTaskSeenBy,
  TFirebaseTimestamp,
  TFirebaseTimestampToISO,
  TTaskSeenBy,
  TTaskType,
} from "../deprecated";
import { TTaskInvoicedTo } from "./taskInvoice";
import { TApiCRUDBy, TApiCrudByWithConverter, TCRUDBy } from "../common/CRUDBy";
import { TApiRelatives, TReference, TRelatives } from "../common/relative";
import { TDate } from "../common/date";
import { TSeenBy } from "../common/seenBy";
import { TReadAccess } from "../common/readAccess";
import { TaskPriority } from "../enums";

export const TTaskBimData = z.object({
  bimFileID: z.string().optional(),
  floorID: z.string().optional(),
  roomID: z.string().optional(),
});
export type ITaskBimData = z.infer<typeof TTaskBimData>;

//This is a combination of TRawDeviation and TApiDeviation but the properties are variable
export const TTask = z
  .object({
    bim: TTaskBimData.optional(),
    /**
     * @deprecated category
     */
    category: z.union([
      z.optional(z.string().or(z.array(z.string()))),
      z.string().optional(),
    ]),
    tags: z.array(z.string()).optional(),
    chats: z
      .record(
        z.string(),
        z.object({
          members: z.array(z.string()).optional(),
        })
      )
      .optional(),
    contractorGroups: z.optional(z.array(z.string())),
    customFields: z.record(z.string(), z.any()).optional(),
    deadline: TDate.optional(),
    description: z.optional(z.string()),
    deviationNumber: z.string().optional(),
    executors: z.optional(z.array(z.string())),
    externalTaskCreationLinkID: z.string().optional(),
    files: z.record(z.string(), TFile).optional(),
    floors: z.array(z.number()).nullish(),
    invoicedTo: TTaskInvoicedTo.optional(),
    isSolved: z.boolean().optional(),
    lastUpdate: TDate.optional(),
    placeDescription: z.optional(z.string()),
    priority: z.nativeEnum(TaskPriority).nullish(),
    propertyID: z.string(),
    relatives: TRelatives.optional(), // This is a data structure, not a sub-collection
    /** @deprecated information here is a duplicate of relatives, use that instead */
    relativeIDs: z.array(z.string()).optional(),
    reportedByName: z.optional(z.string()),
    reportedByExternalEmail: z.optional(z.string()),
    reportDate: TDate.optional(),
    responsibles: z.optional(z.array(z.string())),
    routineID: z.string().nullish(),
    seenBy: TSeenBy.optional(),
    settings: TTaskSettings.optional(),
    skipNotifications: z.optional(z.boolean()),
    startDate: TDate.optional(),
    status: z.number(),
    taskListNumbered: z.boolean().optional(),
    taskOrder: z.array(z.string()).optional(),
    taskTypeID: z.string().optional(),
    title: z.string(),
    totalLinksCreated: z.optional(z.number()),
    type: z.optional(z.number()), // Deprecated, use taskType instead
    unitIDs: z.optional(z.array(z.string())),
    userID: z.optional(z.string()),
    visibility: z.optional(TVisibility),
    // TODO: Remove the fields below once `taskTypeID` is in use
    taskType: z.optional(TTaskType),
    customTaskType: z.union([z.optional(TReference), z.string().optional()]),
    metadata: z
      .object({
        contractorGroups: z.array(TApiContractorGroupMetadata).optional(),
        executors: z.array(TApiUserMetadata).optional(),
        responsibles: z.array(TApiUserMetadata).optional(),
        property: TApiPropertyMetadata.optional(),
        companyNames: z.array(TApiCompanyMetadata).optional(),
        user: TApiUserMetadata.optional(),
        units: z.array(TApiUnitMetadata).optional(),
        routine: TApiRoutineMetadata.optional(),
        relatives: z.array(TApiTaskDetailRelativeMetadata).optional(),
        tags: z.array(TApiCompanyTagMetadata).optional(),
        taskType: TApiCompanyTaskTypeMetadata.optional(),
        externalLinks: z.array(TApiExternalLinkMetadata).optional(),
        invoiceReceiver: TApiInvoiceReceiverMetadata.optional(),
      })
      .optional(),
  })
  .extend(TObjectBaseTDate.shape);
export type ITask = z.infer<typeof TTask>;

export const TTaskUpdate = z
  .object({
    userID: z.string().optional(),
    date: TDate.optional(),
    skipNotifications: z.boolean().optional(),
    comment: z.string().optional(),

    oldProperties: TTask.partial().optional(),
    newProperties: TTask.partial().optional(),

    readAccess: TReadAccess.optional(),
    seenBy: TTask.shape.seenBy.optional(),

    //These fields are needed for external users.
    //These fields are not redundant in the case of normal users, we want to store the user information in case the original user is deleted.
    senderName: z.string().optional(),
    senderRole: z.string().optional(),
    senderEmail: z.string().optional(),
    senderPhone: z.string().optional(),

    //The following fields are a mirror of the deviation object
    invoicedTo: TTaskInvoicedTo.optional(),
    updatedVisibility: TVisibility.optional(),
    unitIDs: TTask.shape.unitIDs.optional(),
    status: TTask.shape.status.optional(),
    priority: TTask.shape.priority.optional(),
    archived: TTask.shape.archived.optional(),
    contractorGroups: TTask.shape.contractorGroups.optional(),
    responsibles: TTask.shape.responsibles.optional(),
    executors: TTask.shape.executors.optional(),
    floors: TTask.shape.floors.optional(),
    description: TTask.shape.description.optional(),
    placeDescription: TTask.shape.placeDescription.optional(),
    startDate: TTask.shape.startDate.optional(),
    deadline: TTask.shape.deadline.optional(),
    /**
     * @deprecated Category
     */
    category: TTask.shape.category.optional(),
    tags: TTask.shape.tags.optional(),
    files: TTask.shape.files.optional(),

    //Deprecated fields
    /** @deprecated use unitIDs instead */
    units: z.array(z.string()).optional(),
    /** @deprecated use newTaskTypeID instead */
    newType: z.string().optional(),
    /** @deprecated use oldTaskTypeID instead */
    oldType: z.string().optional(),
    /** @deprecated use newTaskTypeID instead */
    newCustomTaskType: TReference.optional(),
    /** @deprecated use oldTaskTypeID instead */
    oldCustomTaskType: TReference.optional(),

    //Added and removed data
    addedRelatives: TRelatives.optional(),
    removedRelatives: TRelatives.optional(),
    addedContractorGroups: TTask.shape.contractorGroups.optional(),
    removedContractorGroups: TTask.shape.contractorGroups.optional(),
    addedResponsibles: TTask.shape.responsibles.optional(),
    removedResponsibles: TTask.shape.responsibles.optional(),
    addedExecutors: TTask.shape.executors.optional(),
    removedExecutors: TTask.shape.executors.optional(),

    //Old and new data
    newTitle: z.string().optional(),
    oldTitle: z.string().optional(),
    newDescription: z.string().optional(),
    oldDescription: z.string().optional(),
    newTaskTypeID: z.string().optional(),
    oldTaskTypeID: z.string().optional(),
  })
  .extend(TObjectBaseTDate.shape);

export type ITaskUpdate = z.infer<typeof TTaskUpdate>;

/**
 *
 *
 *
 *
 * -------------- Deprecated below this line -----------------
 *
 *
 *
 *
 */

/**
 * @deprecated use TTask instead
 */
export const TRawDeviation = z
  .object({
    archived: z.optional(z.boolean()),
    bim: TTaskBimData.optional(),
    category: z.optional(z.string().or(z.array(z.string()))),
    tags: z.array(z.string()).optional(),
    chats: z
      .record(
        z.string(),
        z.object({
          members: z.array(z.string()).optional(),
        })
      )
      .optional(),
    contractorGroups: z.optional(z.array(z.string())),
    createdBy: z.optional(TCRUDBy),
    customFields: z.record(z.string(), z.any()).optional(),
    deadline: TFirebaseTimestamp.nullish(),
    deletedBy: z.optional(TCRUDBy),
    description: z.optional(z.string()),
    deviationNumber: z.string().optional(),
    executors: z.optional(z.array(z.string())),
    files: z.record(z.string(), TFile).optional(),
    floors: z.array(z.number()).nullish(),
    invoicedTo: TTaskInvoicedTo.optional(),
    lastUpdate: z.optional(TFirebaseTimestamp),
    placeDescription: z.optional(z.string()),
    priority: z.number().nullish(),
    propertyID: z.string(),
    relatives: TRelatives.optional(), // This is a data structure, not a sub-collection
    /** @deprecated information here is a duplicate of relatives, use that instead */
    relativeIDs: z.array(z.string()).optional(),
    reportedByName: z.optional(z.string()),
    reportedByExternalEmail: z.optional(z.string()),
    reportDate: TFirebaseTimestamp,
    responsibles: z.optional(z.array(z.string())),
    routineID: z.string().nullish(),
    seenBy: TTaskSeenBy.optional(),
    settings: TTaskSettings.optional(),
    skipNotifications: z.optional(z.boolean()),
    startDate: z.optional(TFirebaseTimestamp),
    status: z.number(),
    taskListNumbered: z.boolean().optional(),
    taskOrder: z.array(z.string()).optional(),
    taskTypeID: z.string().optional(),
    title: z.string(),
    totalLinksCreated: z.optional(z.number()),
    type: z.optional(z.number()), // Deprecated, use taskType instead
    unitIDs: z.optional(z.array(z.string())),
    updatedBy: z.optional(TCRUDBy),
    userID: z.optional(z.string()),
    visibility: z.optional(TVisibility),
    // TODO: Remove the fields below once `taskTypeID` is in use
    taskType: z.optional(TTaskType),
    customTaskType: z.optional(TReference),
  })
  .extend(TObjectBase.shape);
/**
 * @deprecated use ITask instead
 */
export type IRawDeviation = z.infer<typeof TRawDeviation>;

/**
 * The deviation object for API responses, with following changes compared to the raw deviation (TRawDeviation):
 * - All dates are ISO strings (z.strings())
 * - All references are string paths (z.strings())
 * - All CRUDBy objects are APICRUDBy objects
 */

/**
 * @deprecated use TTask instead
 */
export const TApiDeviation = TRawDeviation.extend({
  category: z.string().optional(),
  createdBy: TApiCRUDBy,
  customTaskType: z.string().optional(),
  deadline: z.string().optional(),
  deletedBy: TApiCRUDBy.optional(),
  lastUpdate: z.string().optional(),
  relatives: TApiRelatives.optional(),
  reportDate: z.string(),
  seenBy: TApiTaskSeenBy.optional(),
  startDate: z.string().optional(),
  updatedBy: TApiCRUDBy.optional(),
  metadata: TApiTaskMetadataObject.optional(),
});

/**
 * @deprecated use ITask  instead
 */
export type IApiDeviation = z.infer<typeof TApiDeviation>;
/**
 * @deprecated use TTask instead
 */
export const TApiTask = TApiDeviation;
/**
 * @deprecated use ITask instead
 */
export type IApiTask = z.infer<typeof TApiTask>;
/**
 * A zod converter to convert a raw deviation to an API deviation
 */
/**
 * @deprecated use TApiTaskConverter instead
 */
export const TApiDeviationConverter = TRawDeviation.extend({
  category: z
    .any()
    .transform((val) =>
      Array.isArray(val) ? val.join(",") : typeof val === "string" ? val : ""
    )
    .optional(),
  createdBy: TApiCrudByWithConverter.optional(),
  customTaskType: TReferenceToStringConverter.optional(),
  deadline: TFirebaseTimestampToISO.optional(),
  deletedBy: TApiCrudByWithConverter.optional(),
  lastUpdate: TFirebaseTimestampToISO.optional(),
  relatives: z.record(z.string(), TRelativeToStringConverter).optional(),
  reportDate: TFirebaseTimestampToISO,
  seenBy: z
    .record(z.string(), z.object({ date: TFirebaseTimestampToISO }))
    .optional(),
  startDate: TFirebaseTimestampToISO.optional(),
  updatedBy: TApiCrudByWithConverter.optional(),
});

export const TApiTaskConverter = TRawDeviation.extend({
  category: z
    .any()
    .transform((val) =>
      Array.isArray(val) ? val.join(",") : typeof val === "string" ? val : ""
    )
    .optional(),
  createdBy: TApiCrudByWithConverter.optional(),
  customTaskType: TReferenceToStringConverter.optional(),
  deadline: TFirebaseTimestampToISO.optional(),
  deletedBy: TApiCrudByWithConverter.optional(),
  lastUpdate: TFirebaseTimestampToISO.optional(),
  relatives: z.record(z.string(), TRelativeToStringConverter).optional(),
  reportDate: TFirebaseTimestampToISO,
  seenBy: z
    .record(z.string(), z.object({ date: TFirebaseTimestampToISO }))
    .optional(),
  startDate: TFirebaseTimestampToISO.optional(),
  updatedBy: TApiCrudByWithConverter.optional(),
});

export const TApiGetTasksResponse = z.object({
  datas: z.array(TApiTask),
});
export type IApiGetTasksResponse = z.infer<typeof TApiGetTasksResponse>;

/**
 * @deprecated use ITask instead
 */
export type IApiTaskDetail = z.infer<typeof TApiTaskDetail>;
/**
 * @deprecated use TTask instead
 */
export const TApiTaskDetail = TApiTask.extend({
  metadata: TApiTaskDetailMetadataObject.optional(),
});
